import React, { useEffect, useMemo, useRef, useState } from 'react'
// import { timeZoneValues } from 'apps/forms/viewer/base_form'
import { isNotEqual } from 'libs/utils'
import Field from 'components/fields'
import { isValidEmail } from '@eventops/utils/lib/utils/dataCheckers'
const timeZoneHere = Intl.DateTimeFormat().resolvedOptions().timeZone


export function FormField({
  className,
  horizontal,
  horizontalLabelSize,
  fieldProps,
  value,
  value2,
  setValue,
  setValue2,
  setValid,
  currentValues,
  mapping_key = "mapping_key",
  resetTrigger
}) {
  // Check Conditions
  const checkCondition = (condition) => {
    /* conditon: {
     *   type: <conditionType>,
     *   field: <field>,
     *   operator: <conditionOperator>,
     *   value: <any>
     * }
     */
    // Test if is an array of conditions
    if (condition.conditions) {
      return checkConditions(condition.conditions)
    }
    // Get field value
    const fieldValue = currentValues[condition.field]
    if (fieldValue === undefined) {
      return false
    }
    // Make validation
    //console.log(fieldValue + " " + condition.operator + " " + condition.value)
    switch (condition.operator) {
      case "==":
        return (fieldValue == condition.value)
      case "!=":
        return (fieldValue != condition.value)
      case ">=":
        return (fieldValue >= condition.value)
      case "<=":
        return (fieldValue <= condition.value)
      case ">":
        return (fieldValue > condition.value)
      case "<":
        return (fieldValue < condition.value)
      case "()":
        return (fieldValue && fieldValue.includes(condition.value)) ? true : false
      case "!()":
        return !(fieldValue && fieldValue.includes(condition.value)) ? true : false
      case "regex":
        try {
          return (new RegExp(condition.value)).test(String(fieldValue))
        } catch (err) {
          console.error(
            "Form Condition Error: '" + condition.value + "' is not a valid regex.\nThe field '" + fieldProps[mapping_key] + "' will be ignored."
          )
          return false
        }
      default:
        console.error(
          "Form Condition Error: '" + condition.operator + "' is not a valid conditional operator.\nThe field '" + fieldProps[mapping_key] + "' will be ignored."
        )
    }
    return false
  }

  const checkConditions = (conditions) => {
    /* conditions: [<conditon>]
     * conditon: {
     *   type: <conditionType>,
     *   field: <field>,
     *   operator: <conditionOperator>,
     *   value: <any>
     * }
     * 
     */
    // Test if any condition
    if (!conditions || !conditions.length) {
      return true
    }
    // Test for condition type error
    const typeErrors = conditions
      .filter(condition => condition.type !== "or")
      .filter(condition => condition.type !== "and")
    if (typeErrors.length !== 0) {
      const errosList = [...new Set(typeErrors.map(c => c.type))]
      console.error(
        "Form Condition Error: '" + (
          errosList.length === 1 ?
            errosList[0] + "' is not a valid conditional type."
            :
            errosList.join("', '") + "' are not valid conditional types."
        ) + "\nThe field '" + fieldProps[mapping_key] + "' will be ignored."
      )
      return false
    }
    // OR tests
    const orConditions = conditions.filter(condition => condition.type === "or")
    if (orConditions.length !== 0) {
      for (let condition of orConditions) {
        if (checkCondition(condition)) {
          return true
        }
      }
      return false
    }
    // AND tests
    const andConditions = conditions.filter(condition => condition.type === "and")
    for (let condition of andConditions) {
      if (checkCondition(condition) === false) {
        return false
      }
    }
    return true
  }

  const checkFieldConditions = () => checkConditions(fieldProps.conditions)

  // Check Validations
  const checkValidationCondition = (condition) => {
    /* conditon: {
     *   type: <conditionType>,
     *   field: <false(self) or fieldName>,
     *   operator: <conditionOperator>,
     *   checkField: <false(checkValue) or fieldName>,
     *   checkValue: <any>
     *   invalidMessage: <string> // Only works when type in "and"
     *   parseFieldValue?: (value: any) => any // Optional: transform value before comparison
     *   parseCheckValue?: (value: any) => any // Optional: transform checkValue before comparison
     * }
     */
    // Test if is an array of conditions
    if (condition.conditions) {
      return checkValidationConditions(condition.conditions)
    }
    // Get field value
    const _fieldValue = condition.field ? currentValues[condition.field] : value

    const fieldValue = typeof condition?.parseFieldValue === "function" ? (condition.parseFieldValue(_fieldValue) ?? undefined) : _fieldValue

    if (fieldValue === undefined) {
      return [undefined, undefined]
    }
    // Get check value
    const _checkValue = condition.checkField ? currentValues[condition.checkField] : condition.checkValue

    const checkValue = typeof condition?.parseCheckValue === "function" ? (condition.parseCheckValue(_checkValue) ?? undefined) : _checkValue

    if (checkValue === undefined) {
      return [undefined, undefined]
    }
    // Make validation
    //console.log(fieldValue + " " + condition.operator + " " + checkValue)
    switch (condition.operator) {
      case "==":
        return [(fieldValue == checkValue), undefined]
      case "!=":
        return [(fieldValue != checkValue), undefined]
      case ">=":
        return [(fieldValue >= checkValue), undefined]
      case "<=":
        return [(fieldValue <= checkValue), undefined]
      case ">":
        return [(fieldValue > checkValue), undefined]
      case "<":
        return [(fieldValue < checkValue), undefined]
      case "()":
        return [fieldValue.includes(checkValue), undefined]
      case "!()":
        return [!fieldValue.includes(checkValue), undefined]
      case "regex":
        try {
          return [(new RegExp(checkValue)).test(String(fieldValue)), undefined]
        } catch (err) {
          console.error(
            "Form Validation Error: '" + condition.value + "' is not a valid regex." +
            "\nThe field '" + fieldProps[mapping_key] + "' can not be validated."
          )
          return [false, undefined]
        }
      default:
        console.error(
          "Form Validation Error: '" + condition.operator + "' is not a valid conditional operator." +
          "\nThe field '" + fieldProps[mapping_key] + "' can not be validated."
        )
    }
    return [false, undefined]
  }

  const checkValidationConditions = (conditions) => {
    /* conditions: [<conditon>]
     * conditon: {
     *   type: <conditionType>,
     *   field: <false(self) or fieldName>,
     *   operator: <conditionOperator>,
     *   checkField: <false(checkValue) or fieldName>,
     *   checkValue: <any>
     *   invalidMessage: <string> // Only works when type in "and"
     * }
     */
    // Test for condition type error
    const typeErrors = conditions
      .filter(condition => { return condition.type !== "or" })
      .filter(condition => { return condition.type !== "and" })

    if (typeErrors.length !== 0) {
      const errosList = [...new Set(typeErrors.map(c => c.type))]
      console.error(
        "Form Validation Error: '" + (
          errosList.length === 1 ?
            errosList[0] + "' is not a valid conditional type."
            :
            errosList.join("', '") + "' are not valid conditional types."
        ) + "\nThe field '" + fieldProps[mapping_key] + "' can not be validated."
      )
      return [false, undefined]
    }
    let validationResult
    // OR tests
    const orConditions = conditions.filter(condition => { return condition.type === "or" })
    if (orConditions.length !== 0) {
      for (let condition of orConditions) {
        validationResult = checkValidationCondition(condition)
        if (validationResult[0] !== false) {
          return validationResult
        }
      }
      return [false, undefined]
    }
    // AND tests
    const andConditions = conditions.filter(condition => { return condition.type === "and" })
    for (let condition of andConditions) {
      validationResult = checkValidationCondition(condition)
      if (validationResult[0] === false) {
        return [false, validationResult[1] || condition.invalidMessage || undefined]
      }
    }
    return [true, undefined]
  }

  const checkCustomValidation = () => {
    if(typeof fieldProps?.validatorFn !== "function") return [true, undefined];

    return fieldProps.validatorFn(currentValues)
  }

  const checkFieldValidations = () => {
    // Skip Markdowns, iframe and Files
    if (["file", "markdown", "iframe", "divider"].includes(fieldProps.type)) {
      return [true, undefined]
    }
    // If filed not intiated
    if (value === undefined) return [undefined, undefined]
    // Check required validation
    if (fieldProps.required && (Array.isArray(value) ? value.length === 0 : ["", null].includes(value))) {
      return [false, undefined]
    } else if (fieldProps.type === "checkbox") {
      for (let v of fieldProps.values) {
        if (v.required && (!value || !value.includes(v.value))) {
          return [false, v.invalidMessage || undefined]
        }
      }
    }
    // Email validation
    if(fieldProps?.type==='email'){
      if(currentValues[fieldProps?.data]&&!isValidEmail(currentValues[fieldProps?.data])){
        return [false, 'Invalid Email' || undefined]
      }
    }

    // Check custom validation
    const customValidatorResult = checkCustomValidation();

    if(customValidatorResult?.[0] === false) {
      return customValidatorResult;
    }

    // Check for validations conditions
    if (!Array.isArray(fieldProps.validation?.conditions)) {
      return [true, undefined]
    }
    // Check validations
    return checkValidationConditions(fieldProps.validation.conditions)
  }

  // State
  const [[valid, invalidMessage], setValidation] = useState(checkFieldValidations())
  const [render, setRender] = useState(checkFieldConditions())

  // Effects
  useEffect(() => {
    const nextRender = checkFieldConditions()
    setRender(nextRender)
    setValidation(nextRender === false ? [true, undefined] : checkFieldValidations())
  }, [fieldProps, value, currentValues])

  useEffect(() => {
    if (render === false) {
      setValue(fieldProps?.emptyValue)
      setValid(true)
    } else {
      setValid(valid)
    }
  }, [render, valid, resetTrigger])
  return fieldProps.hidden !== true && render && <Field
    {...fieldProps}
    className={className}
    horizontal={horizontal}
    horizontalLabelSize={horizontalLabelSize}
    name={fieldProps[mapping_key]}
    label={fieldProps.label}
    value={value}
    value2={value2}
    values={(fieldProps[mapping_key] === "timezone" && !fieldProps.values?.length) ? null : fieldProps.values}
    valid={valid && fieldProps.validation?.showValid}
    invalidMessage={invalidMessage || fieldProps.invalidMessage || `${fieldProps.label || "This field"} is required`}
    onChange={(event, rawValue) => {
      setValue(event.target.value)
      if (typeof fieldProps.onChange === "function") {
        fieldProps.onChange(event, rawValue)
      }
    }}
    onChange2={(event, rawValue) => {
      setValue2(event.target.value)
      if (typeof fieldProps.onChange === "function") {
        fieldProps.onChange(event, rawValue)
      }
    }}
  />
}

export default function useForm({
  formClassName,
  className,
  horizontal,
  horizontalLabelSize,
  fields,
  initialValues,
  timezone,
  mapping_key = "mapping_key",
  mapping_key2 = "mapping_key2",
  autoFocus,
  readOnly
}) {

  const readInitialValue = (field, mapping_key, prevValues, initialValues) => {
    const initialValue = prevValues && field[mapping_key] in prevValues ? prevValues[field[mapping_key]]
      : initialValues?.[field[mapping_key]] || (field?.[mapping_key] === "timezone" ? timeZoneHere
        : initialValues && field[mapping_key] in initialValues ? initialValues[field[mapping_key]] : "")
    return initialValue !== "" || prevValues ?
      initialValue : field.required ? undefined : ["bool", "boolean"].includes(field.type) ? false : ""
  }
  const readInitialValues = prevValues => Array.isArray(fields) ? fields.reduce((newValues, field) => {
    if (field?.[mapping_key] && !["divider"].includes(field?.type)) {
      // Read Initial Value of mapping_key
      newValues[field[mapping_key]] = readInitialValue(field, mapping_key, prevValues, initialValues)
      // Read Initial Value of mapping_key2
      if (field?.[mapping_key2]) {
        newValues[field[mapping_key2]] = readInitialValue(field, mapping_key2, prevValues, initialValues)
      }
    }
    return newValues
  }, {}) : {}

  // State
  const [savedProps, setSavedProps] = useState({ fields, initialValues })
  const [currentValues, setCurrentValues] = useState(readInitialValues())
  const [currentValids, setCurrentValids] = useState({})
  const [isValid, setIsValid] = useState()
  const [resetTrigger, setResetTrigger] = useState(0)
  const timezoneValue = timezone || currentValues?.timezone || timeZoneHere

  // Functions
  const resetValues = () => {
    setSavedProps({ fields, initialValues })
    setCurrentValues(readInitialValues())
    setCurrentValids({})
    setIsValid()
    setResetTrigger(p => p + 1)
  }

  const setValuesKey = (key) => (value) => setCurrentValues(
    prevValues => ({ ...prevValues, [key]: value })
  )

  const setValidsKey = (key) => (valid) => setCurrentValids(
    prevValids => ({ ...prevValids, [key]: valid })
  )

  const validateForm = () => {
    let isFormValid = true
    for (let field of fields) {
      if (currentValids[field[mapping_key]] === false) {
        isFormValid = false
        break
      }
      if(field.type !== "field_array" && field.type !== "new_field_array") {
        const emptyValue = "emptyValue" in field ? field.emptyValue : ""
        if (currentValids[field[mapping_key]] !== true && currentValues[field[mapping_key]] === undefined && emptyValue !== undefined) {
          isFormValid = false
          setValuesKey(field[mapping_key])(emptyValue)
        }
      }
    }
    setIsValid(isFormValid)
    return isFormValid
  }

  const setValues = values => {
    if (typeof values === "function") {
      setCurrentValues(prev => readInitialValues(values(prev)))
    } else {
      setCurrentValues(readInitialValues(values))
    }
  }

  // Effects
  useEffect(() => {
    if (Object.values(currentValids).some(v => v === undefined)) {
      setIsValid()
    } else if (Object.values(currentValids).some(v => v === false)) {
      setIsValid(false)
    } else {
      setIsValid(true)
    }
  }, [currentValids])

  useEffect(() => {
    if (isNotEqual(savedProps.initialValues, initialValues)) {
      setSavedProps({ fields, initialValues })
      setCurrentValues(readInitialValues())
    } else if (isNotEqual(
      // Need to remove addButton. It's a hook and will make isEqual to crash (too much recursion).
      savedProps.fields?.map(field => ({ ...field, addButton: field.addButton ? true : undefined })),
      fields?.map(field => ({ ...field, addButton: field.addButton ? true : undefined }))
    )) {
      setSavedProps({ fields, initialValues })
      setCurrentValues(prevValues => readInitialValues(prevValues))
    }
  }, [fields, initialValues])

  const focusRef = useRef()
  const focus = () => focusRef.current?.focus()

  // Form
  const form = useMemo(() => {
    return (
      <div className={"form-row" + (formClassName ? ` ${formClassName}` : "")}>
        {Array.isArray(fields) && fields.map(
          (field, key) => field.type === 'field_array' ?
            null
            :!field.hidden && (
              <FormField
                key={field[mapping_key] || key}
                className={`col-6${field.col ? ` col-md-6` : ""}${className ? ` ${className}` : ""}`}
                horizontal={typeof field.horizontal === "boolean" ? field.horizontal : horizontal}
                horizontalLabelSize={horizontalLabelSize}
                mapping_key={mapping_key}
                fieldProps={{
                  ...field,
                  ...(key === 0 ? { inputRef: focusRef, autoFocus } : {}),
                  timezone: timezoneValue,
                  readOnly: readOnly || field.readOnly
                }}
                value={currentValues[field[mapping_key]]}
                value2={currentValues[field[mapping_key2]]}
                setValue={setValuesKey(field[mapping_key])}
                setValue2={setValuesKey(field[mapping_key2])}
                setValid={setValidsKey(field[mapping_key])}
                currentValues={currentValues}
                resetTrigger={resetTrigger}
              />
            )
        )}
      </div>
    )
  }, [fields, currentValues, resetTrigger, autoFocus, readOnly, timezoneValue])

  // Return hooks
  return [
    form,
    currentValues,
    isValid,
    validateForm,
    setValues,
    resetValues,
    focus,
    setIsValid
  ]
}
